import { useCallback, useEffect, useMemo } from "react";
import { ContractId } from "@hashgraph/sdk";
import { ethers } from "ethers";
import { ContractFunctionParameterBuilder } from "./contractFunctionParameterBuilder";
import { hederaAppConfig } from "../../../constants/hedera.contants";
import {
  convertEvmAddressToHederaAddress,
  fetchHederaEvmBalance,
  getProvider,
} from "./evm-calls";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { updateUserData, updateWalletData } from "store";
import { config } from "../../../config/hedera";

const currentNetworkConfig = hederaAppConfig.networks.mainnet;

const METAMASK = "metamask";

const switchToHederaNetwork = async (
  ethereum: any
): Promise<"success" | "autoconnect failed" | "unknown error"> => {
  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: currentNetworkConfig.chainId }],
    });
    return "success";
  } catch (error: any) {
    if (error.code === 4902) {
      try {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainName: `Hedera (${currentNetworkConfig.network})`,
              chainId: currentNetworkConfig.chainId,
              nativeCurrency: {
                name: "HBAR",
                symbol: "HBAR",
                decimals: 18,
              },
              rpcUrls: [currentNetworkConfig.jsonRpcUrl],
            },
          ],
        });
        // Try switching again after adding
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: currentNetworkConfig.chainId }],
        });
        return "success";
      } catch (err: any) {
        if (err?.code === -32603) {
          return "autoconnect failed";
        }
      }
    }

    return "unknown error";
  }
};

type ConnectResult =
  | { type: "no-metamask"; account: null }
  | { type: "no-hedera-account"; account: null }
  | { type: "success"; account: { hedera: string; evm: string } }
  | { type: "error"; account: null };

const useMetamask = () => {
  const provider = useMemo(() => getProvider(), []);
  const signer = useMemo(
    () => (provider ? provider.getSigner() : null),
    [provider]
  );
  const dispatch = useDispatch();
  const { walletAddress, walletAddressHedera } = useSelector(
    (state: any) => state.wallet
  );

  const { data: balance } = useQuery({
    queryKey: [
      "dapp-sdk",
      "hedera",
      "evm",
      "balance",
      {
        accountId: walletAddress,
      },
    ],
    queryFn: () => fetchHederaEvmBalance(walletAddress, config.ids.tokenIdEvm),
  });

  const { data: hederaAddress } = useQuery({
    queryKey: [
      "dapp-sdk",
      "hedera",
      "evm",
      "address",
      {
        accountId: walletAddress,
      },
    ],
    queryFn: () => convertEvmAddressToHederaAddress(walletAddress),
    enabled: walletAddress?.startsWith("0x") && walletAddressHedera == null,
  });

  useEffect(() => {
    if (balance) {
      dispatch(
        updateUserData({
          tokenAmount: balance.hbar,
          tokenXAmount: balance.hbarx,
        })
      );
    }
  }, [balance, dispatch]);

  useEffect(() => {
    if (hederaAddress) {
      dispatch(
        updateWalletData({
          walletAddressHedera: hederaAddress,
        })
      );
    }
  }, [hederaAddress, dispatch]);

  const connect = useCallback(async (): Promise<ConnectResult> => {
    if (window?.ethereum == null) {
      return {
        type: "no-metamask",
        account: null,
      };
    }

    const { ethereum } = window as any;
    await switchToHederaNetwork(ethereum);

    try {
      if (!provider) {
        throw new Error("No provider");
      }

      const evmAccounts = await provider.send("eth_requestAccounts", []);
      const account = await convertEvmAddressToHederaAddress(evmAccounts[0]);

      if (!account) {
        return {
          type: "no-hedera-account",
          account: null,
        };
      }

      return {
        type: "success",
        account: {
          hedera: account,
          evm: evmAccounts[0],
        },
      };
    } catch (err) {
      return {
        type: "error",
        account: null,
      };
    }
  }, [provider]);

  useEffect(() => {
    if (!provider) {
      return () => {};
    }

    provider.listAccounts().then((accounts) => {
      if (!accounts.length) return;

      dispatch(
        updateWalletData({
          connectorID: METAMASK,
          walletName: "MetaMask",
          isConnected: true,
          walletAddress: accounts[0] ?? "",
        })
      );
    });
    const { ethereum } = window as any;
    ethereum?.on("accountsChanged", (accounts: string[]) => {
      if (!accounts.length) return;

      dispatch(
        updateWalletData({
          connectorID: METAMASK,
          walletName: "MetaMask",
          isConnected: true,
          walletAddress: accounts[0] ?? "",
        })
      );
    });
    return () => {
      ethereum?.removeAllListeners("accountsChanged");
    };
  }, [provider, dispatch]);

  const executeContractFunction = async (
    contractId: ContractId,
    functionName: string,
    params: ContractFunctionParameterBuilder,
    gasLimit: number,
    value?: string
  ) => {
    if (!signer) {
      throw new Error("No signer found");
    }

    const abi = [
      `function ${functionName}(${params.buildAbiFunctionParams()})${
        value ? " payable" : ""
      }`,
    ];
    const contract = new ethers.Contract(
      `0x${contractId.toSolidityAddress()}`,
      abi,
      signer
    );
    const tx = await contract[functionName](...params.buildEthersParams(), {
      gasLimit: gasLimit === -1 ? undefined : gasLimit,
      ...(value ? { value: ethers.utils.parseEther(value) } : {}),
    });
    return tx.hash;
  };

  return {
    account: walletAddress,
    connect,
    executeContractFunction,
    signer,
    provider,
  };
};

export { useMetamask };
